import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';
import Icon from './Icon.jsx';

// Meta lead capture — the sheet a lead-ads campaign fills, turned into
// registrations Pulse can show (server/leadFeeds.js).
//
// Dual-surface via `scope`, and deliberately SYMMETRIC: this is the client's own
// marketing data, so they can link their own sheet exactly as Howler would.
//   'admin-client' → Admin → the client → Lead capture
//   'my'           → Settings → Integrations
//
// The design rule here is DON'T PROMISE, SHOW. Linking a spreadsheet is the kind
// of setup that looks fine and silently reads nothing — the wrong tab, an
// unshared file, a renamed column. So "Check the sheet" reads the real file
// before anything is saved and reports what it actually found: which columns it
// matched, how many rows, how many test submissions it will drop, and the first
// real lead as Pulse will store it (ids stripped, date in YOUR timezone). Only
// then is there something to save.
//
// Mobile-first: one stacked column, ≥40px controls, tables scroll inside
// themselves rather than pushing the page sideways.
export default function LeadFeedsCard({ entityId, scope = 'my', suites: suitesProp }) {
  const apiScope = scope === 'my' ? 'my' : 'admin';
  const [view, setView] = useState(null);
  const [denied, setDenied] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [adding, setAdding] = useState(false);
  const [suites, setSuites] = useState(suitesProp || []);

  // The draft feed being linked, and what reading its sheet actually returned.
  const [name, setName] = useState('');
  const [sheetUrl, setSheetUrl] = useState('');
  const [suiteId, setSuiteId] = useState('');
  const [check, setCheck] = useState(null);
  const [checkErr, setCheckErr] = useState('');

  const load = useCallback(() => api.leadFeeds(entityId, apiScope, { days: 365 })
    .then((v) => { if (v && v.error) throw new Error(v.error); setView(v); })
    .catch(() => setDenied(true)), [entityId, apiScope]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (suitesProp) { setSuites(suitesProp); return; }
    api.mySuites().then((s) => setSuites(Array.isArray(s) ? s : (s?.suites || []))).catch(() => setSuites([]));
  }, [suitesProp]);

  if (denied) return null;      // flag off for this client — the section simply isn't there
  if (!view) return <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>Loading registrations…</div>;

  const run = async (key, fn) => {
    setBusy(key); setErr('');
    try {
      const v = await fn();
      if (v && v.error) setErr(v.error); else if (v) setView(v);
    } catch (e) { setErr(String(e.message || e)); }
    setBusy('');
  };

  async function checkSheet() {
    setCheckErr(''); setCheck(null); setBusy('check');
    try {
      const r = await api.leadFeedPreview(entityId, apiScope, { sheetUrl });
      if (r.error) setCheckErr(r.error); else setCheck(r);
    } catch (e) { setCheckErr(String(e.message || e)); }
    setBusy('');
  }
  async function save() {
    await run('save', () => api.leadFeedCreate(entityId, apiScope, { name: name.trim() || 'Meta registrations', sheetUrl, suiteId }));
    setAdding(false); setName(''); setSheetUrl(''); setSuiteId(''); setCheck(null);
  }

  const t = view.totals || { registrations: 0, paid: 0, organic: 0 };
  const money = (n, cur) => (n == null ? '—' : `${cur || 'R'} ${n.toLocaleString()}`);

  return (
    <div style={card}>
      <h3 style={h3}><Icon name="inbox" chip family="engage" /> Meta lead capture</h3>
      <p style={hint}>
        Sign-ups collected on a <b>Meta lead ads</b> form land in a Google Sheet, outside Pulse. Link that sheet here and
        they become real registrations: counted on your dashboards, split into the ones an ad paid for and the ones that
        came in on their own, and priced per lead against what the campaign spent.
      </p>

      {/* What the feeds add up to — the reason the setup exists, first. */}
      {view.feeds.length > 0 && (
        <div style={statRow}>
          <Stat label="Registrations" value={t.registrations.toLocaleString()} />
          <Stat label="From ads" value={t.paid.toLocaleString()} />
          <Stat label="Organic" value={t.organic.toLocaleString()} hint="Sign-ups no ad paid for — they count as registrations, never against cost per lead." />
        </div>
      )}

      {err && <div style={errBox}>{err}</div>}

      {view.feeds.map((f) => (
        <div key={f.id} style={feedRow}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 13.5 }}>{f.name}</span>
            {f.event && <span style={pill}>{f.event}</span>}
          </div>
          {/* A feed that failed its last read is showing yesterday's numbers.
              Say so plainly — the alternative is a confident, wrong count. */}
          {f.status === 'error' ? (
            <div style={warnBox}>
              ⚠ Couldn’t read the sheet: {f.error}
              {f.stale && <div style={{ marginTop: 4, color: 'var(--muted)' }}>Showing the {f.total.toLocaleString()} registrations from the last good read ({when(f.lastSynced)}). Nothing has been lost or zeroed.</div>}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              {f.total.toLocaleString()} registrations · read {when(f.lastSynced)} · re-read hourly
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <button style={btn} disabled={!!busy} onClick={() => run(`sync-${f.id}`, () => api.leadFeedSync(entityId, apiScope, f.id))}>
              {busy === `sync-${f.id}` ? 'Reading…' : '↻ Read now'}
            </button>
            {suites.length > 0 && (
              <select
                style={{ ...input, width: 'auto', minWidth: 150 }}
                value={f.suiteId || ''}
                onChange={(e) => run(`ev-${f.id}`, () => api.leadFeedUpdate(entityId, apiScope, f.id, { suiteId: e.target.value }))}
              >
                <option value="">No event</option>
                {suites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
            <button
              style={{ ...btn, color: 'var(--error)' }}
              disabled={!!busy}
              onClick={() => { if (confirm(`Remove "${f.name}"? Its registrations stop counting on dashboards. The Google Sheet itself is untouched.`)) run(`del-${f.id}`, () => api.leadFeedRemove(entityId, apiScope, f.id)); }}
            >Remove</button>
          </div>
        </div>
      ))}

      {/* Cost per lead, per campaign — leads the ad bought ÷ what it spent. */}
      {view.campaigns.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>What the ads bought</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={table}>
              <thead><tr><th style={th}>Campaign</th><th style={thNum}>Leads</th><th style={thNum}>Spend</th><th style={thNum}>Cost/lead</th></tr></thead>
              <tbody>
                {view.campaigns.map((c) => (
                  <tr key={c.campaignId}>
                    <td style={td}>{c.name}</td>
                    <td style={tdNum}>{c.leads.toLocaleString()}</td>
                    <td style={tdNum}>{money(c.spend, c.currency)}</td>
                    <td style={tdNum}>{money(c.costPerLead, c.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ ...hint, marginTop: 6 }}>Spend comes from the Meta ads sync; a blank means Pulse holds no spend for that campaign yet.</div>
        </div>
      )}

      {!adding ? (
        <button style={{ ...btn, marginTop: 14 }} onClick={() => setAdding(true)}>+ Link a lead sheet</button>
      ) : (
        <div style={{ ...feedRow, marginTop: 14 }}>
          <label style={label}>Name</label>
          <input style={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. BCW 2027 registrations" />

          <label style={label}>Google Sheet link</label>
          <input style={input} value={sheetUrl} onChange={(e) => { setSheetUrl(e.target.value); setCheck(null); }} placeholder="https://docs.google.com/spreadsheets/d/…" />
          <div style={hint}>
            Pulse reads it through your Google Drive connection, so the sheet can stay private — pick it under
            Integrations → Google Drive → “Pick files or folders”. Without Drive connected the sheet has to be shared
            with “anyone with the link”.
          </div>

          {suites.length > 0 && (
            <>
              <label style={label}>Event (optional)</label>
              <select style={input} value={suiteId} onChange={(e) => setSuiteId(e.target.value)}>
                <option value="">No event</option>
                {suites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
            <button style={btn} disabled={!sheetUrl.trim() || busy === 'check'} onClick={checkSheet}>
              {busy === 'check' ? 'Reading the sheet…' : 'Check the sheet'}
            </button>
            <button style={{ ...btn, ...(check ? primary : null) }} disabled={!check || !!busy} onClick={save} title={check ? '' : 'Check the sheet first'}>
              {busy === 'save' ? 'Saving…' : 'Save & start counting'}
            </button>
            <button style={btn} onClick={() => { setAdding(false); setCheck(null); setCheckErr(''); }}>Cancel</button>
          </div>

          {checkErr && <div style={errBox}>{checkErr}</div>}

          {/* The whole point of the check: what it ACTUALLY read. */}
          {check && (
            <div style={okBox}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Read {check.rows.toLocaleString()} rows.</div>
              <div>
                Matched columns: {Object.keys(check.mapping).length ? Object.entries(check.mapping).map(([k, v]) => `${k} → ${v}`).join(' · ') : 'none'}
              </div>
              {(check.skipped.test > 0 || check.skipped['no-date'] > 0) && (
                <div style={{ marginTop: 4 }}>
                  Skipping {check.skipped.test > 0 ? `${check.skipped.test} test submission${check.skipped.test === 1 ? '' : 's'}` : ''}
                  {check.skipped.test > 0 && check.skipped['no-date'] > 0 ? ' and ' : ''}
                  {check.skipped['no-date'] > 0 ? `${check.skipped['no-date']} row(s) with no readable submission time` : ''}.
                </div>
              )}
              {check.sample[0] ? (
                <div style={{ marginTop: 6 }}>
                  First real lead, as Pulse will store it: <b>{check.sample[0].name || check.sample[0].email || 'unnamed'}</b>
                  {' · '}{check.sample[0].localDate} ({check.timezone})
                  {check.sample[0].campaignName ? ` · ${check.sample[0].campaignName}` : ' · organic (no ad)'}
                </div>
              ) : (
                <div style={{ marginTop: 6 }}>No usable leads in the sheet yet — that’s fine for a campaign that hasn’t run, but check the columns above if you expected some.</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, hint: tip }) {
  return (
    <div style={stat} title={tip || ''}>
      <div style={{ fontSize: 19, fontWeight: 800 }}>{value}</div>
      <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{label}</div>
    </div>
  );
}

const when = (iso) => {
  if (!iso) return 'never';
  const mins = Math.round((Date.now() - Date.parse(iso)) / 60000);
  if (!Number.isFinite(mins)) return 'never';
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

const card = { background: 'var(--card)', border: '1px solid var(--hairline)', borderRadius: 12, padding: 16, marginBottom: 14 };
const h3 = { display: 'flex', alignItems: 'center', gap: 7, fontSize: 15, fontWeight: 700, margin: '0 0 8px' };
const hint = { fontSize: 12, color: 'var(--muted)', lineHeight: 1.5, margin: '0 0 8px' };
const label = { display: 'block', fontSize: 12, fontWeight: 700, margin: '10px 0 4px' };
const input = { width: '100%', boxSizing: 'border-box', minHeight: 40, padding: '9px 10px', borderRadius: 9, border: '1px solid var(--hairline)', background: 'var(--bg)', color: 'inherit', fontSize: 13 };
const btn = { minHeight: 40, padding: '9px 14px', borderRadius: 9, border: '1px solid var(--hairline)', background: 'var(--bg)', color: 'inherit', fontSize: 13, cursor: 'pointer' };
const primary = { background: 'var(--brand, #111)', color: '#fff', borderColor: 'transparent', fontWeight: 700 };
const feedRow = { border: '1px solid var(--hairline)', borderRadius: 10, padding: 12, marginTop: 10 };
const pill = { fontSize: 11, padding: '2px 8px', borderRadius: 999, background: 'var(--bg)', border: '1px solid var(--hairline)', color: 'var(--muted)' };
const statRow = { display: 'flex', gap: 10, flexWrap: 'wrap', margin: '4px 0 10px' };
const stat = { flex: '1 1 90px', minWidth: 90, padding: '10px 12px', borderRadius: 10, background: 'var(--bg)', border: '1px solid var(--hairline)' };
const errBox = { fontSize: 12.5, color: 'var(--error)', marginTop: 8, lineHeight: 1.5 };
const warnBox = { fontSize: 12.5, color: 'var(--error)', marginTop: 4, lineHeight: 1.5 };
const okBox = { fontSize: 12.5, marginTop: 10, padding: 10, borderRadius: 9, background: 'var(--bg)', border: '1px solid var(--hairline)', lineHeight: 1.5 };
const table = { width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 380 };
const th = { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--hairline)', color: 'var(--muted)', fontWeight: 600 };
const thNum = { ...th, textAlign: 'right' };
const td = { padding: '6px 8px', borderBottom: '1px solid var(--hairline)' };
const tdNum = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
